import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import styled from 'styled-components'
import { ZoningType } from '@popety-io/types'
import {
  Map as MapComponent,
  createAnalysisBounds,
  type MapLayer,
} from '../../organisms/Map'
import { MapPanels } from '../../organisms/Map/Map.panels'
import { useFullscreen } from '../../organisms/Map/hooks/core/useFullscreen'
import { Icon } from '../../atoms/Icon'
import { AnalysisMapProps } from './AnalysisMap.types'
import {
  getMapStyleUrl,
  getMapStyleIdFromUrl,
  DEFAULT_MAP_STYLE_ID,
} from '../../../config/mapStylesConfig'
import { MapSectionProvider, useMapSection } from './context'
import { useSectionLayers } from './hooks'
import { getSectionGroupTitle } from './config'
import type {
  LayerGroup,
  Layer,
} from '../../molecules/LayersPanel/LayersPanel.types'
import { SunlightLayerControls } from '../../molecules/LayersPanel/components'
import {
  listingsToGeoJSON,
  amenityPOIsToGeoJSON,
  transportStopsToGeoJSON,
  isochronesToGeoJSON,
  evStationsToGeoJSON,
  highwayAccessToGeoJSON,
  roofDataToGeoJSON,
  buildingPermitsToGeoJSON,
  transactionsToGeoJSON,
} from './utils/mapDataTransformers'
import { createSolarPotentialFillExpression } from './utils/energyLayerHelpers'

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
`

const FullscreenButton = styled.button<{ $isFullscreen?: boolean }>`
  position: absolute;
  top: 16px;
  left: 16px;
  width: 40px;
  height: 40px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: none;
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 5;

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
    box-shadow: ${({ theme }) => theme.shadows.lg};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    top: 12px;
    left: 12px;
    width: 36px;
    height: 36px;
  }
`

// Default coordinates for Geneva, Switzerland if no coordinates provided
const DEFAULT_CENTER: [number, number] = [6.1424, 46.2022]

/**
 * Internal component with map section context access
 */
const AnalysisMapInternal: React.FC<AnalysisMapProps> = ({
  coordinates = DEFAULT_CENTER,
  analysisType = 'land',
  onResizeStart,
  children,
  className,
  mapHeight = '100%',
  uiConfig,
  customLayers,
  onLayerToggle: _onLayerToggle,
  geoJson,
  buildingGeoJson,
  buildingHeight,
  buildingClassification,
  customRadius,
  autoFitBounds = true,
  activeSection,
  sectionData,
  hoveredItemId: propHoveredItemId,
  selectedItemId: propSelectedItemId,
  setSelectedItemId: propSetSelectedItemId,
  flyToCoordinates: propFlyToCoordinates,
  onItemClick: propOnItemClick,
}) => {
  // Access map section context
  const {
    setActiveSection,
    setSectionData,
    sectionData: contextSectionData,
    hasActiveSectionLayers,
    activeSection: contextActiveSection,
    layerStates: dynamicLayerStates,
    toggleLayer: toggleDynamicLayer,
    setLayerOpacity,
    hoveredItemId: contextHoveredItemId,
    selectedItemId: contextSelectedItemId,
    flyToCoordinates: contextFlyToCoordinates,
    onItemClick: contextOnItemClick,
    selectedZoningType,
    setSelectedZoningType,
    disabledZoningTypes,
    isSatelliteZoning,
  }: any = useMapSection()

  // Use props if provided, otherwise fall back to context
  const hoveredItemId = propHoveredItemId ?? contextHoveredItemId
  const selectedItemId = propSelectedItemId ?? contextSelectedItemId
  const setSelectedItemId = propSetSelectedItemId ?? undefined
  const flyToCoordinates = propFlyToCoordinates ?? contextFlyToCoordinates
  const onItemClick = propOnItemClick ?? contextOnItemClick

  // Get dynamic layer configurations for the active section
  const sectionLayerConfigs = useSectionLayers()

  // Sync activeSection from props (needed for section navigation)
  useEffect(() => {
    setActiveSection(activeSection || null)
  }, [activeSection, setActiveSection])

  // Sync sectionData from props when it changes
  // Merge with existing data to preserve user-controlled values (like sunlight slider)
  useEffect(() => {
    if (sectionData) {
      setSectionData((prev: any) => ({
        ...prev,
        ...sectionData,
      }))
    }
  }, [sectionData, setSectionData])

  // Sync zoningData specifically when it changes (needed for zoning section)
  useEffect(() => {
    if (sectionData?.zoningData) {
      setSectionData((prev: any) => ({
        ...prev,
        zoningData: sectionData.zoningData,
      }))
    }
  }, [sectionData?.zoningData, setSectionData])

  // Auto-select historical imagery layer when a history event is clicked
  useEffect(() => {
    // Only run for history section with a selected year
    if (
      contextActiveSection === 'history' &&
      sectionData?.selectedYear &&
      typeof sectionData.selectedYear === 'string'
    ) {
      const yearToSelect = sectionData.selectedYear
      const layerIdToSelect = `historical-imagery-${yearToSelect}`

      // Find all historical imagery layers
      const historicalImageryLayers = sectionLayerConfigs.filter((config) =>
        config.id.startsWith('historical-imagery-')
      )

      // Disable all historical imagery layers except the selected one
      historicalImageryLayers.forEach((layerConfig) => {
        const shouldEnable = layerConfig.id === layerIdToSelect
        toggleDynamicLayer(layerConfig.id, shouldEnable)
      })
    }
  }, [
    contextActiveSection,
    sectionData?.selectedYear,
    sectionLayerConfigs,
    toggleDynamicLayer,
  ])

  // Handle fly to coordinates when a listing is selected
  const [flyToPosition, setFlyToPosition] = useState<
    [number, number] | undefined
  >(undefined)

  useEffect(() => {
    if (flyToCoordinates) {
      // Handle both formats: [lng, lat] or { coordinates: [lng, lat], zoom?: number }
      if (Array.isArray(flyToCoordinates)) {
        setFlyToPosition([...flyToCoordinates] as [number, number])
      } else if (flyToCoordinates.coordinates) {
        setFlyToPosition([...flyToCoordinates.coordinates] as [number, number])
      }
    }
  }, [flyToCoordinates])

  // Handle dynamic layer click (e.g., marker click)
  const handleDynamicLayerClick = useCallback(
    (feature: any, layerId: string) => {
      // When a listing marker is clicked, find the full listing object and call the handler
      if (layerId === 'listings-markers' && onItemClick && sectionData) {
        const listings = (sectionData as any).listings || []
        const listingId = feature.id
        const listing = listings.find((l: any) => l.id === listingId)

        if (listing) {
          onItemClick(listing)
        }
      }

      // When a building permit marker is clicked, find the full permit object and call the handler
      if (
        layerId === 'building-permits-markers' &&
        onItemClick &&
        sectionData
      ) {
        const permits = (sectionData as any).building_permits || []
        const permitId = feature.id
        const permit = permits.find((p: any) => p.id === permitId)

        if (permit) {
          onItemClick(permit)
        }
      }

      // When a transaction marker is clicked, find the full transaction object and call the handler
      if (layerId === 'transaction-points' && onItemClick && sectionData) {
        const transactions = (sectionData as any).transactions || []
        const transactionId = feature.id
        const transaction = transactions.find(
          (t: any) => t.id === transactionId
        )

        if (transaction) {
          onItemClick(transaction)
        }
      }
    },
    [onItemClick, sectionData]
  )

  // State for layers panel
  const [layersPanelOpen, setLayersPanelOpen] = useState(false)
  const [legendPanelOpen, setLegendPanelOpen] = useState(false)
  const [selectedThematicLayer, setSelectedThematicLayer] = useState('')
  const [currentMapStyle, setCurrentMapStyle] =
    useState<string>(DEFAULT_MAP_STYLE_ID)

  // Track if a style change was user-initiated to prevent restoration callback from overriding it
  const userInitiatedStyleChangeRef = useRef(false)

  // Fullscreen functionality
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const { isFullscreen, toggleFullscreen } = useFullscreen(
    mapContainerRef as any
  )

  // State for layer visibility
  const [layerStates, setLayerStates] = useState<Record<string, boolean>>({
    // Map Details layers
    streets: true,
    labels: true,
    terrain: false,
    contours: false,
    // Land Property layers
    'parcel-boundaries': true,
    'land-dimension-metrics': false,
    // Building Property layers
    'building-footprints': true,
    'building-3d': false,
    'building-dimension-metrics': false,
  })

  // Legend configurations for isochrone layers
  const legendConfigs = useMemo(
    () => ({
      'transport-walking-isochrones': {
        type: 'gradient' as const,
        title: 'Walking Travel Time',
        labels: ['5 min', '10 min', '15 min', '30 min'],
        unit: '',
      },
      'transport-cycling-isochrones': {
        type: 'gradient' as const,
        title: 'Cycling Travel Time',
        labels: ['5 min', '10 min', '15 min', '30 min'],
        unit: '',
      },
      'transport-driving-isochrones': {
        type: 'gradient' as const,
        title: 'Driving Travel Time',
        labels: ['5 min', '10 min', '15 min', '30 min'],
        unit: '',
      },
    }),
    []
  )

  // Auto-show/hide legend when isochrone layers are toggled
  useEffect(() => {
    const isochroneLayers = [
      'transport-walking-isochrones',
      'transport-cycling-isochrones',
      'transport-driving-isochrones',
    ]

    // Find which isochrone layer is enabled (only one can be enabled due to exclusive group)
    const enabledIsochrone = isochroneLayers.find(
      (layerId) => dynamicLayerStates[layerId]?.enabled
    )

    if (enabledIsochrone) {
      // Show legend with the enabled isochrone
      setSelectedThematicLayer(enabledIsochrone)
      setLegendPanelOpen(true)
    } else if (isochroneLayers.includes(selectedThematicLayer)) {
      // Hide legend if the previously selected isochrone is now disabled
      setSelectedThematicLayer('')
      setLegendPanelOpen(false)
    }
  }, [dynamicLayerStates, selectedThematicLayer])

  // Handle zoning map style switching
  const preZoningStyleRef = useRef<string | null>(null)
  const lastAppliedStyleRef = useRef<string | null>(null)
  const prevSectionRef = useRef<string | null>(null)

  // Effect 1: Track user changes to Map base (to update restoration target)
  useEffect(() => {
    const isZoningSection =
      contextActiveSection === 'zoning_data' ||
      contextActiveSection === 'zoning_rules'

    // If we're in zoning and the style changed to a non-zoning style,
    // it means the user manually changed Map base - save it for restoration
    if (
      isZoningSection &&
      !currentMapStyle.startsWith('popety-zoning') &&
      currentMapStyle !== lastAppliedStyleRef.current
    ) {
      preZoningStyleRef.current = currentMapStyle
    }
  }, [currentMapStyle, contextActiveSection])

  // Effect 2: Handle zoning section transitions (entering/leaving)
  useEffect(() => {
    const isZoningSection =
      contextActiveSection === 'zoning_data' ||
      contextActiveSection === 'zoning_rules'
    const wasZoningSection =
      prevSectionRef.current === 'zoning_data' ||
      prevSectionRef.current === 'zoning_rules'

    // Detect transition: ENTERING zoning section
    if (!wasZoningSection && isZoningSection) {
      // Save current style when entering zoning
      // If already on a zoning style, save the default style as fallback
      const styleToSave = currentMapStyle.startsWith('popety-zoning')
        ? DEFAULT_MAP_STYLE_ID
        : currentMapStyle

      preZoningStyleRef.current = styleToSave
    }

    // Detect transition: LEAVING zoning section
    if (wasZoningSection && !isZoningSection) {
      // Restore saved style when leaving zoning (with fallback to default)
      const styleToRestore = preZoningStyleRef.current || DEFAULT_MAP_STYLE_ID

      lastAppliedStyleRef.current = styleToRestore
      setCurrentMapStyle(styleToRestore)
      preZoningStyleRef.current = null
    }

    // Update previous section for next comparison
    prevSectionRef.current = contextActiveSection
  }, [contextActiveSection])

  // Effect 3: Apply zoning style WHILE in zoning section
  useEffect(() => {
    const isZoningSection =
      contextActiveSection === 'zoning_data' ||
      contextActiveSection === 'zoning_rules'

    if (!isZoningSection) return

    const satelliteZoningEnabled =
      dynamicLayerStates['zoning-satellite-map-style']?.enabled
    const targetStyle = satelliteZoningEnabled
      ? 'popety-zoning-satellite'
      : 'popety-zoning'

    // Apply the target style (check against ref to avoid reading state in effect)
    if (lastAppliedStyleRef.current !== targetStyle) {
      lastAppliedStyleRef.current = targetStyle
      setCurrentMapStyle(targetStyle)
    }
  }, [contextActiveSection, dynamicLayerStates])

  // Create appropriate bounding box for the analysis
  // Use useMemo to ensure bbox only recalculates when dependencies change
  const bbox = useMemo(() => {
    return createAnalysisBounds(
      coordinates,
      analysisType,
      customRadius,
      geoJson
    )
  }, [coordinates, analysisType, customRadius, geoJson])

  // Choose preset based on analysis type

  // Default UI configuration for analysis maps
  const defaultUiConfig = {
    showResizeHandle: true,
    showLayerSwitcher: true,
    showMapControls: true,
    showLayersButton: analysisType !== 'building', // Buildings might not need layers panel
    showLegendButton: analysisType === 'land', // Land analysis benefits from legend
    show3DButton: true,
    showPrintButton: analysisType === 'estimate',
    showZoomControls: true,
    showDrawingTools: true, // Enable drawing tools (including measurement) for all analysis maps
    showLocationButton: false, // Disable fly-to-location for analysis maps
    controlPositions: {
      layerSwitcher: 'top-left' as const,
      mapControls: 'top-right' as const,
      zoomControls: 'bottom-left' as const,
      drawingFAB: 'bottom-right' as const,
      locationFAB: 'bottom-right' as const,
    },
    ...uiConfig, // Allow overrides
  }

  const handleResizeStart = () => {
    onResizeStart?.()
  }

  // Handle layer toggle
  const handleLayerToggle = (layerId: string, enabled: boolean) => {
    setLayerStates((prev) => ({
      ...prev,
      [layerId]: enabled,
    }))

    // Call optional external handler
    _onLayerToggle?.(layerId, enabled)
  }

  // Handle map style change and dynamic layer radio selections from layer panel
  const handleMapStyleChange = (layerIdOrStyleId: string, value?: string) => {
    // If value is provided, this is a dynamic layer radio selection
    if (value) {
      handleLayerRadioSelect(layerIdOrStyleId, value)
    } else {
      // Otherwise, it's a map style change - track it for Effect 1
      userInitiatedStyleChangeRef.current = true // Mark as user-initiated
      lastAppliedStyleRef.current = layerIdOrStyleId
      setCurrentMapStyle(layerIdOrStyleId)
    }
  }

  // Legacy handler for backward compatibility (toggles between standard and satellite)
  const handleChangeMapStyle = () => {
    setCurrentMapStyle((current) =>
      current === 'popetyio-default' ? 'mapbox-satellite' : 'popetyio-default'
    )
  }

  // Create default map layers based on analysis type
  const defaultMapLayers = useMemo<MapLayer[]>(() => {
    // Helper to get layer state
    const getLayerState = (layerId: string, defaultState: boolean): boolean => {
      return layerStates[layerId] !== undefined
        ? layerStates[layerId]
        : defaultState
    }

    // Detect if we're using a Mapbox Standard style
    const isStandardStyle = currentMapStyle.includes('standard')

    // Define Land Property layers
    const landPropertyLayers: MapLayer[] = [
      {
        id: 'parcel-boundaries',
        name: 'Parcel Boundaries',
        enabled: getLayerState('parcel-boundaries', true),
        visible: getLayerState('parcel-boundaries', true),
      },
      {
        id: 'land-dimension-metrics',
        name: 'Dimension Metrics',
        enabled: getLayerState('land-dimension-metrics', false),
        visible: getLayerState('land-dimension-metrics', false),
      },
    ]

    // Define Building Property layers
    const buildingPropertyLayers: MapLayer[] = [
      {
        id: 'building-footprints',
        name: 'Footprint',
        enabled: getLayerState('building-footprints', true),
        visible: getLayerState('building-footprints', true),
      },
      {
        id: 'building-3d',
        name: 'Building 3D',
        enabled: getLayerState('building-3d', false),
        visible: getLayerState('building-3d', false),
      },
      {
        id: 'building-dimension-metrics',
        name: 'Dimension Metrics',
        enabled: getLayerState('building-dimension-metrics', false),
        visible: getLayerState('building-dimension-metrics', false),
      },
    ]

    // Define Map Details layers
    const mapDetailsLayers: MapLayer[] = [
      {
        id: 'streets',
        name: 'Streets',
        enabled: getLayerState('streets', true),
        visible: getLayerState('streets', true),
        disabled: isStandardStyle, // Streets cannot be toggled in Standard styles
        disabledReason: isStandardStyle
          ? 'Street visibility cannot be controlled in Standard map styles. Streets are always part of the base map.'
          : undefined,
      },
      {
        id: 'labels',
        name: 'Labels',
        enabled: getLayerState('labels', true),
        visible: getLayerState('labels', true),
      },
      {
        id: 'terrain',
        name: 'Terrain',
        enabled: getLayerState('terrain', false),
        visible: getLayerState('terrain', false),
      },
      {
        id: 'contours',
        name: 'Contours',
        enabled: getLayerState('contours', false),
        visible: getLayerState('contours', false),
      },
    ]

    // Combine all layers
    const allLayers = [
      ...landPropertyLayers,
      ...buildingPropertyLayers,
      ...mapDetailsLayers,
    ]

    // Return custom layers if provided, otherwise return all layers
    return customLayers || allLayers
  }, [layerStates, currentMapStyle, customLayers])

  /**
   * Build dynamic layer groups for the layer panel based on active section
   */
  const dynamicLayerGroups = useMemo<LayerGroup[]>(() => {
    if (!contextActiveSection || !hasActiveSectionLayers()) {
      return []
    }

    const groupTitle =
      getSectionGroupTitle(contextActiveSection) || 'Dynamic Layers'

    // Special handling for restrictions section - create layers dynamically from data
    if (contextActiveSection === 'restrictions' && sectionData?.restrictions) {
      const restrictions = sectionData.restrictions as any[]

      // Get unique RDPPF types from the data
      const rdppfTypesMap = new Map<string, { id: string; name: string }>()
      restrictions.forEach((r) => {
        if (r.rdppfTypeId && r.type) {
          rdppfTypesMap.set(r.rdppfTypeId, {
            id: r.rdppfTypeId,
            name: r.type,
          })
        }
      })

      // Get shared opacity from first layer or use default (30%)
      const firstLayerId = `restrictions-${Array.from(rdppfTypesMap.keys())[0]}`
      const sharedOpacity = dynamicLayerStates[firstLayerId]?.opacity ?? 30

      // Create independent layer toggles for each RDPPF type
      const restrictionLayers: Layer[] = Array.from(rdppfTypesMap.values()).map(
        (rdppfType) => {
          const layerId = `restrictions-${rdppfType.id}`
          const layerState = dynamicLayerStates[layerId] || {
            enabled: true,
            opacity: sharedOpacity,
          }

          return {
            id: layerId,
            label: rdppfType.name,
            enabled: layerState.enabled,
            type: 'checkbox' as const,
            opacity: sharedOpacity, // Use shared opacity
            showOpacity: false, // Don't show individual opacity controls
          }
        }
      )

      // Return a single group with all restriction layers
      if (restrictionLayers.length > 0) {
        return [
          {
            id: 'restrictions',
            title: groupTitle,
            layers: restrictionLayers,
            groupType: 'dynamic', // Mark as dynamic to enable quick actions
            groupRenderType: 'multiple-checkbox',
            showGroupOpacity: true, // Show group-level opacity slider
            groupOpacity: sharedOpacity, // Shared opacity value
          },
        ]
      }

      return []
    }

    // Group layers by exclusive group
    const exclusiveGroups = new Map<string, typeof sectionLayerConfigs>()
    const independentLayers: typeof sectionLayerConfigs = []

    sectionLayerConfigs.forEach((layerConfig) => {
      if (layerConfig.exclusiveGroup) {
        const group = exclusiveGroups.get(layerConfig.exclusiveGroup) || []
        group.push(layerConfig)
        exclusiveGroups.set(layerConfig.exclusiveGroup, group)
      } else {
        independentLayers.push(layerConfig)
      }
    })

    const layerGroups: LayerGroup[] = []

    // Add exclusive groups as separate LayerGroups with exclusive-dropdown render type
    exclusiveGroups.forEach((groupLayers, groupId) => {
      // Determine if this group should show "No layer" option
      const shouldShowNoLayerOption =
        groupId === 'demographics-choropleth' ||
        groupId === 'employment-choropleth' ||
        groupId === 'transport-isochrones' ||
        groupId === 'purchase-market-choropleth' ||
        groupId === 'rental-market-choropleth' ||
        groupId === 'noise-choropleth' ||
        groupId === 'zoning-layers' ||
        groupId === 'historical-imagery'

      // Find which layer is currently enabled
      const enabledLayer = groupLayers.find((lc) => {
        const state = dynamicLayerStates[lc.id]
        // If state exists, use its enabled value; otherwise fall back to defaultEnabled
        const isEnabled =
          state !== undefined ? state.enabled : lc.defaultEnabled
        return isEnabled
      })

      // Determine selected value:
      // - If no layer is enabled and group supports "No layer", use 'none'
      // - Otherwise use enabled layer or default to first layer
      const selectedLayerId =
        !enabledLayer && shouldShowNoLayerOption
          ? 'none'
          : enabledLayer?.id || groupLayers[0].id

      // Get opacity from the selected layer (not just the first enabled one)
      // Find the selected layer config to get its default opacity
      const selectedLayerConfig = groupLayers.find(
        (lc) => lc.id === selectedLayerId
      )
      const selectedLayerState =
        selectedLayerId !== 'none' && dynamicLayerStates[selectedLayerId]
          ? dynamicLayerStates[selectedLayerId]
          : {
              enabled: true,
              opacity: selectedLayerConfig?.defaultOpacity || 30,
            }

      // Create a single layer with options for the dropdown
      const getLayerLabel = () => {
        if (groupId === 'demographics-choropleth') return 'Demographics Layer'
        if (groupId === 'employment-choropleth') return 'Employment Layer'
        if (groupId === 'purchase-market-choropleth')
          return 'Purchase Market Layer'
        if (groupId === 'rental-market-choropleth') return 'Rental Market Layer'
        if (groupId === 'noise-choropleth') return 'Noise Layer'
        if (groupId === 'zoning-layers') return 'Zoning Layer'
        if (groupId === 'historical-imagery') return 'Historical Imagery'
        return groupId
      }

      const getGroupTitle = () => {
        if (groupId === 'demographics-choropleth') return 'Demographics'
        if (groupId === 'employment-choropleth') return 'Employment'
        if (groupId === 'purchase-market-choropleth') return 'Purchase Market'
        if (groupId === 'rental-market-choropleth') return 'Rental Market'
        if (groupId === 'noise-choropleth') return 'Noise Analysis'
        if (groupId === 'zoning-layers') return 'Zoning Layer'
        if (groupId === 'historical-imagery')
          return 'Historical Satellite Images'
        return groupId
      }

      // Check if the selected layer config has opacity support
      const selectedLayerHasOpacity = selectedLayerConfig?.hasOpacity !== false

      // Map layer IDs to zoning types for disabled state check
      const layerIdToZoningType: Record<string, ZoningType> = {
        'zoning-general-only': 'general',
        'zoning-cantonal-only': 'cantonal',
        'zoning-local-only': 'local',
        'zoning-partial-only': 'partial',
      }

      const exclusiveLayer = {
        id: groupId,
        label: getLayerLabel(),
        enabled: true, // Radio groups are always "enabled" (one option is always selected)
        type: 'radio' as const,
        renderAs: 'select' as const, // Use dropdown for exclusive groups
        selectedValue: selectedLayerId,
        opacity: selectedLayerState.opacity,
        showOpacity: selectedLayerHasOpacity, // Only show if layer config allows opacity
        options: groupLayers.map((lc) => {
          // Check if this option should be disabled (for zoning type filter only)
          const isZoningTypeFilter = groupId === 'zoning-type-filter'
          const zoningType = layerIdToZoningType[lc.id]
          const isDisabled =
            isZoningTypeFilter &&
            zoningType &&
            disabledZoningTypes.includes(zoningType)

          return {
            value: lc.id,
            label: lc.name,
            description: lc.description,
            disabled: isDisabled,
          }
        }),
      }

      layerGroups.push({
        id: groupId,
        title: getGroupTitle(),
        groupType: 'dynamic' as const,
        groupRenderType: 'exclusive-dropdown' as const,
        sectionName: groupTitle,
        icon: 'layers',
        defaultExpanded: true,
        showNoLayerOption: shouldShowNoLayerOption,
        layers: [exclusiveLayer],
      })
    })

    // Add independent layers as a multiple-checkbox group if any exist
    if (independentLayers.length > 0) {
      const layers = independentLayers.map((layerConfig) => {
        const layerState = dynamicLayerStates[layerConfig.id] || {
          enabled: layerConfig.defaultEnabled,
          opacity: layerConfig.defaultOpacity || 30,
        }

        return {
          id: layerConfig.id,
          label: layerConfig.name,
          enabled: layerState.enabled,
          type: 'toggle' as const,
          opacity: layerState.opacity,
          showOpacity: layerConfig.hasOpacity || false,
        }
      })

      layerGroups.push({
        id: 'independent-layers',
        title: 'Layers',
        groupType: 'dynamic' as const,
        groupRenderType: 'multiple-checkbox' as const,
        sectionName: groupTitle,
        icon: 'layers',
        defaultExpanded: true,
        layers,
      })
    }

    return layerGroups
  }, [
    contextActiveSection,
    hasActiveSectionLayers,
    sectionData,
    dynamicLayerStates,
    sectionLayerConfigs,
    disabledZoningTypes,
  ])

  /**
   * Build dynamic layers data from section data and layer states
   */
  const getDynamicLayersData = () => {
    if (!contextActiveSection || !sectionData) {
      return []
    }

    const dynamicLayers: Array<{
      id: string
      type: 'markers' | 'heatmap' | 'polygon' | 'line'
      geoJson: any
      visible: boolean
      config?: {
        clustering?: boolean
        clusterMaxZoom?: number
        colors?: string[]
        opacity?: number
        hoveredId?: string | number | null
        selectedId?: string | number | null
        fillExpression?: any
        strokeColor?: string
        strokeWidth?: number
        lineOnly?: boolean
        lineDasharray?: number[]
      }
    }> = []

    // Handle listings markers layer
    if (
      contextActiveSection === 'purchase_listings' ||
      contextActiveSection === 'rental_listings'
    ) {
      const listings = (sectionData as any).listings || []

      if (listings.length > 0) {
        // Transform listings to GeoJSON
        const listingsGeoJson = listingsToGeoJSON(listings)

        const listingsMarkersConfig = sectionLayerConfigs.find(
          (config) => config.id === 'listings-markers'
        )
        const layerState = dynamicLayerStates['listings-markers'] || {
          enabled: listingsMarkersConfig?.defaultEnabled || true,
          opacity: listingsMarkersConfig?.defaultOpacity || 30,
        }

        dynamicLayers.push({
          id: 'listings-markers',
          type: 'markers',
          geoJson: listingsGeoJson,
          visible: layerState.enabled,
          config: {
            clustering: listingsMarkersConfig?.render?.clustering || true,
            clusterMaxZoom: listingsMarkersConfig?.render?.clusterMaxZoom || 14,
            colors: listingsMarkersConfig?.render?.colors || [
              '#28A745',
              '#FFC107',
              '#DC3545',
            ],
            opacity: layerState.opacity,
            hoveredId: hoveredItemId,
            selectedId: selectedItemId,
          },
        })
      }
    }

    // Handle building permits markers layer
    if (contextActiveSection === 'building_permits') {
      const permits = (sectionData as any).building_permits || []

      if (permits.length > 0) {
        // Transform building permits to GeoJSON
        const permitsGeoJson = buildingPermitsToGeoJSON(permits)

        // For now, use default config since building permits may not have layer config yet
        const permitsMarkersConfig = sectionLayerConfigs.find(
          (config) => config.id === 'building-permits-markers'
        )
        const layerState = dynamicLayerStates['building-permits-markers'] || {
          enabled: permitsMarkersConfig?.defaultEnabled ?? true,
          opacity: permitsMarkersConfig?.defaultOpacity || 30,
        }

        dynamicLayers.push({
          id: 'building-permits-markers',
          type: 'markers',
          geoJson: permitsGeoJson,
          visible: layerState.enabled,
          config: {
            clustering: permitsMarkersConfig?.render?.clustering ?? true,
            clusterMaxZoom: permitsMarkersConfig?.render?.clusterMaxZoom || 14,
            colors: permitsMarkersConfig?.render?.colors || ['#8b5cf6'], // Purple color for building permits
            opacity: layerState.opacity,
            hoveredId: hoveredItemId,
            selectedId: selectedItemId,
          },
        })
      }
    }

    // Handle transactions markers layer
    if (contextActiveSection === 'transactions') {
      const transactions = (sectionData as any).transactions || []

      if (transactions.length > 0) {
        // Transform transactions to GeoJSON
        const transactionsGeoJson = transactionsToGeoJSON(transactions)

        const transactionsMarkersConfig = sectionLayerConfigs.find(
          (config) => config.id === 'transaction-points'
        )
        const layerState = dynamicLayerStates['transaction-points'] || {
          enabled: transactionsMarkersConfig?.defaultEnabled ?? true,
          opacity: transactionsMarkersConfig?.defaultOpacity || 30,
        }

        dynamicLayers.push({
          id: 'transaction-points',
          type: 'markers',
          geoJson: transactionsGeoJson,
          visible: layerState.enabled,
          config: {
            clustering: transactionsMarkersConfig?.render?.clustering ?? true,
            clusterMaxZoom:
              transactionsMarkersConfig?.render?.clusterMaxZoom || 14,
            colors: transactionsMarkersConfig?.render?.colors || ['#10b981'], // Green color for transactions
            opacity: layerState.opacity,
            hoveredId: hoveredItemId,
            selectedId: selectedItemId,
          },
        })
      }
    }

    // Handle amenities markers layer - create 9 separate layers with separate sources (one per category)
    if (contextActiveSection === 'amenities') {
      const amenityPOIs = (sectionData as any).amenities || []

      if (amenityPOIs.length > 0) {
        // Create 9 separate layers, each with its own filtered GeoJSON source
        const categories = [
          'shopping',
          'grocery',
          'culture',
          'dining',
          'education',
          'wellness',
          'healthcare',
          'transport',
          'services',
        ]

        categories.forEach((category) => {
          const layerId = `amenities-${category}`
          const layerConfig = sectionLayerConfigs.find(
            (config) => config.id === layerId
          )
          const layerState = dynamicLayerStates[layerId] || {
            enabled: layerConfig?.defaultEnabled !== false,
            opacity: layerConfig?.defaultOpacity || 30,
          }

          // Filter POIs by category and create separate GeoJSON for this category
          const categoryPOIs = amenityPOIs.filter(
            (poi: any) => poi.category === category
          )
          const categoryGeoJson = amenityPOIsToGeoJSON(categoryPOIs)

          dynamicLayers.push({
            id: layerId,
            type: 'markers',
            geoJson: categoryGeoJson, // Separate GeoJSON for each category
            visible: layerState.enabled,
            config: {
              clustering: layerConfig?.render?.clustering || true,
              clusterMaxZoom: layerConfig?.render?.clusterMaxZoom || 14,
              colors: layerConfig?.render?.colors || ['#808080'],
              opacity: layerState.opacity,
              hoveredId: hoveredItemId,
              selectedId: selectedItemId,
              // Category filter no longer needed since source is pre-filtered
            },
          })
        })
      }
    }

    // Handle Transport & Access section - 8 layers (3 stops + 3 isochrones + 2 infrastructure)
    if (contextActiveSection === 'transport') {
      const transportData = (sectionData as any)?.transport

      // 1. Transport stops (bus, tram, train) - 3 separate layers
      const publicTransportStops = transportData?.publicTransportStops || []

      if (publicTransportStops.length > 0 && transportData?.services) {
        const categorizedStops =
          transportData.services.categorizePublicTransportStops(
            publicTransportStops
          )

        // Create 3 separate stop layers with pre-filtered sources
        const stopTypes: Array<'bus' | 'tram' | 'train'> = [
          'bus',
          'tram',
          'train',
        ]

        stopTypes.forEach((stopType) => {
          const layerId = `transport-${stopType}-stops`
          const layerConfig = sectionLayerConfigs.find(
            (config) => config.id === layerId
          )
          const layerState = dynamicLayerStates[layerId] || {
            enabled: layerConfig?.defaultEnabled !== false,
            opacity: layerConfig?.defaultOpacity || 30,
          }

          const stops = categorizedStops[stopType] || []
          const stopsGeoJson = transportStopsToGeoJSON(stops, stopType)

          dynamicLayers.push({
            id: layerId,
            type: 'markers',
            geoJson: stopsGeoJson,
            visible: layerState.enabled,
            config: {
              clustering: layerConfig?.render?.clustering || true,
              clusterMaxZoom: layerConfig?.render?.clusterMaxZoom || 14,
              colors: layerConfig?.render?.colors || ['#808080'],
              opacity: layerState.opacity,
              hoveredId: hoveredItemId,
              selectedId: selectedItemId,
            },
          })
        })
      }

      // 2. Travel time isochrones (walking, cycling, driving) - 3 mutually exclusive layers
      const isochrones = transportData?.isochrones

      if (isochrones) {
        const travelModes: Array<'walking' | 'cycling' | 'driving'> = [
          'walking',
          'cycling',
          'driving',
        ]

        travelModes.forEach((mode) => {
          const layerId = `transport-${mode}-isochrones`
          const layerConfig = sectionLayerConfigs.find(
            (config) => config.id === layerId
          )
          const layerState = dynamicLayerStates[layerId] || {
            enabled: layerConfig?.defaultEnabled !== false,
            opacity: layerConfig?.defaultOpacity || 30,
          }

          // Only add layer if it's enabled (mutual exclusivity handled by layer panel)
          if (layerState.enabled) {
            const isochroneGeoJson = isochronesToGeoJSON(isochrones, mode)

            dynamicLayers.push({
              id: layerId,
              type: 'polygon',
              geoJson: isochroneGeoJson,
              visible: true,
              config: {
                colors: layerConfig?.render?.colors || [
                  '#22c55e',
                  '#eab308',
                  '#f97316',
                  '#ef4444',
                ],
                opacity: layerState.opacity,
              },
            })
          }
        })
      }

      // 3. EV Charging stations
      const evStations =
        transportData?.activeMobilityMetrics?.evStationsNearby || []

      if (evStations.length > 0) {
        const layerId = 'transport-ev-charging'
        const layerConfig = sectionLayerConfigs.find(
          (config) => config.id === layerId
        )
        const layerState = dynamicLayerStates[layerId] || {
          enabled: layerConfig?.defaultEnabled !== false,
          opacity: layerConfig?.defaultOpacity || 30,
        }

        const evGeoJson = evStationsToGeoJSON(evStations)

        dynamicLayers.push({
          id: layerId,
          type: 'markers',
          geoJson: evGeoJson,
          visible: layerState.enabled,
          config: {
            clustering: layerConfig?.render?.clustering || true,
            clusterMaxZoom: layerConfig?.render?.clusterMaxZoom || 14,
            colors: layerConfig?.render?.colors || ['#8b5cf6'],
            opacity: layerState.opacity,
            hoveredId: hoveredItemId,
            selectedId: selectedItemId,
          },
        })
      }

      // 4. Highway access points
      const highwayAccess =
        transportData?.roadAccessMetrics?.allHighwayAccess || []

      if (highwayAccess.length > 0) {
        const layerId = 'transport-highway-access'
        const layerConfig = sectionLayerConfigs.find(
          (config) => config.id === layerId
        )
        const layerState = dynamicLayerStates[layerId] || {
          enabled: layerConfig?.defaultEnabled !== false,
          opacity: layerConfig?.defaultOpacity || 30,
        }

        const highwayGeoJson = highwayAccessToGeoJSON(highwayAccess)

        dynamicLayers.push({
          id: layerId,
          type: 'markers',
          geoJson: highwayGeoJson,
          visible: layerState.enabled,
          config: {
            clustering: layerConfig?.render?.clustering || true,
            clusterMaxZoom: layerConfig?.render?.clusterMaxZoom || 14,
            colors: layerConfig?.render?.colors || ['#10b981'],
            opacity: layerState.opacity,
            hoveredId: hoveredItemId,
            selectedId: selectedItemId,
          },
        })
      }
    }

    // Handle energy section - solar potential roof polygons
    if (contextActiveSection === 'energy') {
      const roofData = (sectionData as any).roofData

      if (roofData && roofData.features && roofData.features.length > 0) {
        const layerId = 'energy-solar-potential'
        const layerConfig = sectionLayerConfigs.find(
          (config) => config.id === layerId
        )
        const layerState = dynamicLayerStates[layerId] || {
          enabled: layerConfig?.defaultEnabled !== false,
          opacity: layerConfig?.defaultOpacity || 60,
        }

        const roofGeoJson = roofDataToGeoJSON(roofData)

        dynamicLayers.push({
          id: layerId,
          type: 'polygon',
          geoJson: roofGeoJson,
          visible: layerState.enabled,
          config: {
            colors: layerConfig?.render?.colors || [
              '#2DC5FF',
              '#FFFC09',
              '#FCAA00',
              '#FA5502',
              '#A80200',
            ],
            opacity: layerState.opacity,
            fillExpression: createSolarPotentialFillExpression(),
          },
        })
      }
    }

    // Handle demographics section - isochrone layer for travel time area
    if (contextActiveSection === 'demographics') {
      const demographicsData = (sectionData as any)?.demographics
      const isochrone = demographicsData?.isochrone

      if (isochrone && isochrone.features && isochrone.features.length > 0) {
        const layerId = 'demographics-isochrone'
        const layerConfig = sectionLayerConfigs.find(
          (config) => config.id === layerId
        )
        const layerState = dynamicLayerStates[layerId] || {
          enabled: layerConfig?.defaultEnabled !== false,
          opacity: layerConfig?.defaultOpacity || 30,
        }

        dynamicLayers.push({
          id: layerId,
          type: 'polygon',
          geoJson: isochrone,
          visible: layerState.enabled,
          config: {
            colors: layerConfig?.render?.colors || ['#3b82f6'],
            opacity: layerState.opacity,
            strokeColor: layerConfig?.render?.strokeColor || '#2563eb',
            strokeWidth: layerConfig?.render?.strokeWidth || 2,
            lineOnly: true, // Render as dashed border only, no fill
            lineDasharray: [4, 4], // Dash pattern
          },
        })
      }
    }

    // Handle employment section - isochrone layer for travel time area
    if (contextActiveSection === 'employment') {
      const employmentData = (sectionData as any)?.employment
      const isochrone = employmentData?.isochrone

      if (isochrone && isochrone.features && isochrone.features.length > 0) {
        const layerId = 'employment-isochrone'
        const layerConfig = sectionLayerConfigs.find(
          (config) => config.id === layerId
        )
        const layerState = dynamicLayerStates[layerId] || {
          enabled: layerConfig?.defaultEnabled !== false,
          opacity: layerConfig?.defaultOpacity || 30,
        }

        dynamicLayers.push({
          id: layerId,
          type: 'polygon',
          geoJson: isochrone,
          visible: layerState.enabled,
          config: {
            colors: layerConfig?.render?.colors || ['#8b5cf6'],
            opacity: layerState.opacity,
            strokeColor: layerConfig?.render?.strokeColor || '#7c3aed',
            strokeWidth: layerConfig?.render?.strokeWidth || 2,
            lineOnly: true, // Render as dashed border only, no fill
            lineDasharray: [4, 4], // Dash pattern
          },
        })
      }
    }

    return dynamicLayers
  }

  /**
   * Handle toggle for both base layers and dynamic layers
   */
  const handleAllLayersToggle = (layerId: string, enabled: boolean) => {
    // Check if this is a dynamic layer
    const isDynamicLayer = sectionLayerConfigs.some(
      (config) => config.id === layerId
    )

    // Also check if it's a restriction layer (dynamically created)
    const isRestrictionLayer = layerId.startsWith('restrictions-')

    if (isDynamicLayer || isRestrictionLayer) {
      toggleDynamicLayer(layerId, enabled)
    } else {
      // Base layer toggle
      handleLayerToggle(layerId, enabled)
    }
  }

  // Handle map style restoration from persistence
  const handleMapStyleRestore = useCallback((mapStyleUrl: string) => {
    // If this is right after a user-initiated change, ignore the restoration
    if (userInitiatedStyleChangeRef.current) {
      userInitiatedStyleChangeRef.current = false // Reset the flag
      return
    }

    // Convert the style URL back to style ID for the dropdown
    const styleId = getMapStyleIdFromUrl(mapStyleUrl)
    setCurrentMapStyle(styleId)
  }, [])

  const handleLayerOpacityChange = (layerId: string, opacity: number) => {
    // Check if this is an exclusive group (radio) - the layerId might be the group ID
    const exclusiveGroupLayers = sectionLayerConfigs.filter(
      (config) => config.exclusiveGroup === layerId
    )

    if (exclusiveGroupLayers.length > 0) {
      // This is an exclusive group - apply opacity to ALL layers in the group
      // This ensures that when switching between options, the opacity is preserved
      exclusiveGroupLayers.forEach((layerConfig) => {
        setLayerOpacity(layerConfig.id, opacity)
      })
    } else {
      // Regular layer - set opacity directly
      setLayerOpacity(layerId, opacity)
    }
  }

  const handleLayerRadioSelect = (groupId: string, selectedLayerId: string) => {
    // Map of zoning type filter layer IDs to zoning types
    const zoningTypeMap: Record<string, 'all' | ZoningType> = {
      'zoning-all': 'all',
      'zoning-general-only': 'general',
      'zoning-cantonal-only': 'cantonal',
      'zoning-local-only': 'local',
      'zoning-partial-only': 'partial',
    }

    // Detect zoning type filter changes and update context
    if (groupId === 'zoning-type-filter' && selectedLayerId in zoningTypeMap) {
      const zoningType = zoningTypeMap[selectedLayerId]
      setSelectedZoningType(zoningType)
      // Fall through to existing layer toggle logic
    }

    // Find all layers in this exclusive group
    const exclusiveGroupLayers = sectionLayerConfigs.filter(
      (config) => config.exclusiveGroup === groupId
    )

    // If "No layer" is selected (value = 'none'), disable all layers in the group
    if (selectedLayerId === 'none') {
      exclusiveGroupLayers.forEach((layerConfig) => {
        toggleDynamicLayer(layerConfig.id, false)
      })
      return
    }

    // Otherwise, disable all layers in the group except the selected one
    exclusiveGroupLayers.forEach((layerConfig) => {
      const shouldEnable = layerConfig.id === selectedLayerId
      toggleDynamicLayer(layerConfig.id, shouldEnable)
    })
  }

  /**
   * Render custom controls for specific layers
   * Currently supports sunlight study layers
   */
  const renderCustomControls = useCallback(
    (selectedLayerId: string) => {
      // Sunlight study layers
      if (
        selectedLayerId === 'sunlight-shadow-simulation' ||
        selectedLayerId === 'sunlight-hours'
      ) {
        const currentDate =
          contextSectionData?.sunlightConfig?.date || new Date()

        return (
          <SunlightLayerControls
            selectedLayerId={selectedLayerId}
            currentDate={currentDate}
            dateKey={currentDate.getTime().toString()}
            onDateChange={(newDate) => {
              setSectionData((prev: any) => ({
                ...prev,
                sunlightConfig: { date: newDate },
              }))
            }}
          />
        )
      }

      return null
    },
    [contextSectionData, setSectionData]
  )

  return (
    <MapContainer ref={mapContainerRef} className={className}>
      {children ? (
        children
      ) : (
        <>
          {/* Fullscreen Button */}
          <FullscreenButton
            onClick={toggleFullscreen}
            $isFullscreen={isFullscreen}
            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            <Icon name={isFullscreen ? 'close' : 'maximize'} size="small" />
          </FullscreenButton>

          <MapComponent
            persistenceContext={`analysis-${analysisType}`}
            center={coordinates}
            bbox={bbox}
            zoom={18}
            autoFitBounds={autoFitBounds}
            bboxTransitionDuration={1000}
            height={mapHeight}
            mapStyle={getMapStyleUrl(currentMapStyle)}
            showControls={defaultUiConfig.showMapControls}
            showNavigation={defaultUiConfig.showZoomControls}
            enableDrawing={defaultUiConfig.showDrawingTools}
            enableMeasurement={true} // Enable measurement tools for drawing functionality
            showDimensionControl={defaultUiConfig.show3DButton}
            showBuiltInPrintControl={defaultUiConfig.showPrintButton}
            layers={defaultMapLayers}
            onLayerToggle={handleLayerToggle}
            baseLayerStates={layerStates}
            onBaseLayerStatesRestore={setLayerStates}
            onMapStyleRestore={handleMapStyleRestore}
            showFlyToPosition={true}
            flyToPosition={flyToPosition}
            layersPanelOpen={layersPanelOpen}
            setLayersPanelOpen={setLayersPanelOpen}
            legendPanelOpen={legendPanelOpen}
            setLegendPanelOpen={setLegendPanelOpen}
            onChangeMapStyle={handleChangeMapStyle}
            mapStylePanelOpen={currentMapStyle === 'satellite'}
            geoJson={geoJson}
            buildingGeoJson={buildingGeoJson}
            buildingHeight={buildingHeight}
            buildingClassification={buildingClassification}
            dynamicLayersData={getDynamicLayersData()}
            onDynamicLayerClick={handleDynamicLayerClick}
            mapRestrictions={{
              minZoom: 8,
              maxZoom: 20,
              scrollZoom: true,
              doubleClickZoom: true,
              dragRotate: true,
              keyboard: true,
            }}
            setSelectedItemId={setSelectedItemId}
            activeSection={contextActiveSection}
            sectionData={contextSectionData}
            demographicsLayerStates={dynamicLayerStates}
            employmentLayerStates={dynamicLayerStates}
            selectedZoningType={selectedZoningType}
            disabledZoningTypes={disabledZoningTypes}
            isSatelliteZoning={isSatelliteZoning}
            historicalImageryLayerState={(() => {
              // Find which historical imagery layer is enabled
              const enabledYear = Object.keys(dynamicLayerStates).find(
                (key) =>
                  key.startsWith('historical-imagery-') &&
                  dynamicLayerStates[key]?.enabled
              )
              if (enabledYear) {
                // Extract year from layer ID (e.g., 'historical-imagery-2024' -> '2024')
                const year = enabledYear.replace('historical-imagery-', '')
                return {
                  selectedYear: year,
                  opacity: dynamicLayerStates[enabledYear]?.opacity || 80,
                }
              }
              return { selectedYear: 'disabled', opacity: 80 }
            })()}
          />

          {/* Analysis-specific Map Panels (Layers and Legend) */}
          <MapPanels
            showLayerSelector={defaultUiConfig.showLayersButton}
            showBuiltInLayersPanel={true}
            layersPanelOpen={layersPanelOpen}
            setLayersPanelOpen={setLayersPanelOpen}
            mapLayers={defaultMapLayers}
            onLayerToggle={handleAllLayersToggle}
            onLayerSelect={handleMapStyleChange}
            onLayerOpacityChange={handleLayerOpacityChange}
            currentMapStyle={currentMapStyle}
            selectedThematicLayer={selectedThematicLayer}
            setLegendPanelOpen={setLegendPanelOpen}
            dynamicLayerGroups={dynamicLayerGroups}
            renderCustomControls={renderCustomControls}
            showLegend={defaultUiConfig.showLegendButton}
            showBuiltInLegendPanel={true}
            legendPanelOpen={legendPanelOpen}
            legendConfigs={legendConfigs}
          />

          {/* Resize handle for split screen layouts */}
          <div
            style={{
              position: 'absolute',
              left: '-4px',
              top: 0,
              bottom: 0,
              width: '8px',
              cursor: 'col-resize',
              background: 'transparent',
              zIndex: 1001,
              transition: 'all 0.2s ease',
            }}
            onMouseDown={(e) => {
              e.preventDefault()
              handleResizeStart()
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#00ade6'
              e.currentTarget.style.opacity = '0.15'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.opacity = '1'
            }}
          />
        </>
      )}
    </MapContainer>
  )
}

/**
 * AnalysisMap component with MapSectionProvider wrapper
 *
 * This is the main export that provides the section context to the internal component.
 */
export const AnalysisMap: React.FC<AnalysisMapProps> = (props) => {
  return (
    <MapSectionProvider
      initialSection={props.activeSection || null}
      initialData={props.sectionData || null}
    >
      <AnalysisMapInternal {...props} />
    </MapSectionProvider>
  )
}
